import React, { FC, useEffect, useRef } from 'react'
import { InputComponent } from 'Pages/landings/common/components/Input/CreditsInput'
import { Button } from 'Pages/landings/common/components/Button'
import { FieldNames, ProductSlugs } from 'Pages/landings/common/constants'
import { landingApi } from 'Pages/landings/common/api'
import {
	required,
	validateCyrillicsWithHyphen,
	validateInn,
} from 'Pages/landings/common/validators'
import {
	getMinCompanyExistErrorText,
	getParamsFromUrl,
	normalizePhone,
	submitLeadAndOrder,
} from 'Pages/landings/common/helpers'
import { useForm } from 'Pages/landings/common/hooks/useForm'
import { Product, Region } from 'Pages/landings/common/types'
import { DynamicAgreement } from 'Pages/landings/common/components/DynamicAgreement'
import './style.scss'

interface ComponentProps {
	id?: string
	regionName?: string
	isClient?: boolean
	onBack: () => void
	regionId?: number
	sendMessage?: (
		typeMessage: string,
		title: string,
		subTitle: string,
		id: string,
		isClient: boolean
	) => void
	regionsNoDivisions?: Region[]
	products?: Product[]
	token: string
}

export const FormComponent: FC<ComponentProps> = ({
	isClient,
	regionName,
	onBack,
	id,
	regionId,
	sendMessage,
	regionsNoDivisions = [],
	products,
	token,
}) => {
	const { formValue, createChangeHandler, getFormError, getFormValue, submit } = useForm<
		Record<string, string> & Record<'is_open_score', boolean>
	>()

	const orderID = useRef<number | null>(null)

	const className = {
		modal: 'modal-form',
		form: 'landing-form',
		buttonBlack: 'landing-button-black',
		formCheckbox: 'landing-form-checkbox',
		agreement: 'landing-form-agreement',
		agreementLink: 'landing-form-agreement-link',
		maskInput: 'landing-form-mask-input',
		buttonWhite: 'landing-button-white',
		landingBlockFormRow: 'landing-block-form_row',
		landingBlockFormCol: 'landing-block-form_col',
	}

	const beforeShowForm = () => {
		if (id === null || id === undefined) {
			sendMessage('error', 'Заявка не создана: некорректная ссылка', '', null, isClient)
			return false
		}
		if (regionsNoDivisions.find((region) => region.id === Number(regionId))) {
			sendMessage(
				'error',
				'Ошибка при отправке',
				'К сожалению, в вашем регионе продукт недоступен.',
				null,
				isClient
			)
			return false
		}
		return true
	}

	const handleSubmit = () => {
		if (!beforeShowForm()) return
		const callback = (res) => {
			const newId = res?.id ?? null
			const isBussinesLanding = window.location.href.includes('landing/credit-bussines-card')
			const subtitleCredit = 'Ожидайте СМС'
			const subtitleBussines =
				isClient === null || isClient === undefined
					? 'В ближайшее время с вами свяжется специалист банка'
					: 'С вами свяжется менеджер банка и проконсультирует вас.'

			sendMessage(
				'success',
				'Заявка создана',
				isBussinesLanding ? subtitleBussines : subtitleCredit,
				newId,
				isClient
			)
		}
		const errorCallback = (data) => {
			const error = Object.keys(data[0] ?? {})[0]
			const bodyError = (codeError: string): string => {
				switch (codeError) {
					case 'blocked_by_me':
						return 'Спасибо! Заявка уже была отправлена ранее'
					case 'blocked_by_other':
						return 'В настоящее время подача заявки невозможна'
					case 'min_company_existence_check_failed':
						return getMinCompanyExistErrorText(data[0])
					default:
						return 'Заявка не создана по прочим причинам. Для уточнения обратитесь на support@dasreda.ru.'
				}
			}
			sendMessage('error', 'Ошибка при отправке', bodyError(error), null, isClient)
		}
		const values = formValue
		const newValues = {
			first_name: values?.first_name ?? '.',
			last_name: values?.last_name ?? '.',
			region_id: regionId,
			product_ids: [],
			phone: values.phone,
			inn: values.inn,
			is_open_score: values.is_open_score,
			...getParamsFromUrl(),
		}
		if (values.is_open_score) {
			newValues.product_ids = products.map((product) => product.id)
		} else {
			newValues.product_ids = products
				.filter(
					(product: Product): boolean => product.slug !== ProductSlugs.PAKET_USLUG_LEGKIY_START
				)
				.map((product: Product): number => product.id)
		}
		submitLeadAndOrder(orderID.current, newValues, id, regionName, callback, errorCallback)
	}

	useEffect(() => {
		const agreementInfo = async () => {
			const info = await landingApi.getPartnerInfo(token)
			orderID.current = info.id
		}
		if (token) agreementInfo()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token])
	return (
		<div className={className.form}>
			<div>
				<InputComponent
					onChange={createChangeHandler({
						fieldName: FieldNames.FIRST_NAME,
						validation: [required(), validateCyrillicsWithHyphen()],
						initialValue: '',
					})}
					placeholder="Имя"
					type="text"
					name={FieldNames.FIRST_NAME}
					value={getFormValue(FieldNames.FIRST_NAME)}
					error={getFormError(FieldNames.FIRST_NAME)}
				/>
			</div>
			<div>
				<InputComponent
					onChange={createChangeHandler({
						fieldName: FieldNames.LAST_NAME,
						validation: [required(), validateCyrillicsWithHyphen()],
						initialValue: '',
					})}
					placeholder="Фамилия"
					type="text"
					name={FieldNames.LAST_NAME}
					value={getFormValue(FieldNames.LAST_NAME)}
					error={getFormError(FieldNames.LAST_NAME)}
				/>
			</div>
			<div>
				<InputComponent
					onChange={createChangeHandler({
						fieldName: FieldNames.MIDDLE_NAME,
						validation: [validateCyrillicsWithHyphen()],
						initialValue: '',
					})}
					placeholder="Отчество"
					type="text"
					name={FieldNames.MIDDLE_NAME}
					value={getFormValue(FieldNames.MIDDLE_NAME)}
					error={getFormError(FieldNames.MIDDLE_NAME)}
				/>
			</div>
			<div>
				<InputComponent
					onChange={createChangeHandler({
						fieldName: FieldNames.PHONE,
						validation: [required()],
						initialValue: '',
						normalize: normalizePhone,
					})}
					placeholder="Телефон"
					mask="+7 (999) 999-99-99"
					maskChar="_"
					type="text"
					name={FieldNames.PHONE}
					value={getFormValue(FieldNames.PHONE)}
					error={getFormError(FieldNames.PHONE)}
				/>
			</div>
			<div>
				<InputComponent
					onChange={createChangeHandler({
						fieldName: FieldNames.INN,
						validation: [required(), validateInn()],
						initialValue: '',
					})}
					placeholder="ИНН организации или ИП"
					type="text"
					name={FieldNames.INN}
					value={getFormValue(FieldNames.INN)}
					error={getFormError(FieldNames.INN)}
				/>
			</div>
			<DynamicAgreement orderID={orderID} token={token} />
			<div className={className.landingBlockFormRow}>
				<div>
					<Button onClick={() => submit(handleSubmit)}>Отправить заявку</Button>
				</div>
				<div>
					<Button type="white" onClick={onBack}>
						Назад
					</Button>
				</div>
			</div>
		</div>
	)
}
